<!-- The title row carries the close affordance. `.lmc-dialog-title` supplies the
     typography only — _dialogs.scss leaves layout to each dialog, and a title with a button
     in it needs its own. -->
<div class="search-dialog-header">
    <h2 mat-dialog-title class="lmc-dialog-title">Search Transactions</h2>
    <button mat-icon-button mat-dialog-close type="button"
            class="close-button"
            aria-label="Close search"
            matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</div>

<mat-dialog-content class="search-dialog-content">

    <!-- The search box is the whole control surface: there is no Search button, because
         results follow the typing (debounced, see the component). cdkFocusInitial puts the
         cursor here on open so the operator can type straight away. -->
    <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
        <mat-label>Loan number or customer name</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <!-- type="text", not "search": WebKit draws its own clear (x) inside a search
             input, which sat next to the Material one below for two identical buttons. -->
        <input matInput
               type="text"
               cdkFocusInitial
               autocomplete="off"
               [formControl]="searchControl"
               placeholder="e.g. 1234567 or Jane Doe" />
        <button *ngIf="searchControl.value"
                mat-icon-button matSuffix type="button"
                aria-label="Clear search"
                (click)="onClear()">
            <mat-icon>close</mat-icon>
        </button>
    </mat-form-field>

    <!-- Results. A hit can live in any batch, so the row carries enough to answer
         "what happened to this payment" on its own, and the Batch column is the way into
         the page where it can be acted on. -->
    <div class="results-area">

        <table mat-table
               [dataSource]="results"
               class="flat-table search-table"
               *ngIf="results.length && !errorMessage">

            <ng-container matColumnDef="loanNumber">
                <th mat-header-cell *matHeaderCellDef>Loan No.</th>
                <td mat-cell *matCellDef="let row">{{ row.loanNumber || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="customerName">
                <th mat-header-cell *matHeaderCellDef>Customer</th>
                <td mat-cell *matCellDef="let row">
                    <span class="customer-cell">
                        <span>{{ row.customerName || '—' }}</span>
                        <!-- The row matched on the loan account's name, not this one — say so,
                             or the hit looks like a bug (searching "Kelvin" and being shown
                             "MORRIS KAMAU", whose account name is "MORRIS KELVIN KAMAU"). -->
                        <span class="matched-as" *ngIf="matchedAccountName(row) as accountName">
                            matched “{{ accountName }}”
                        </span>
                    </span>
                </td>
            </ng-container>

            <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">
                    <span class="status-badge" [style.--status-color]="getStatusColor(row.status)">
                        {{ row.status }}
                    </span>
                </td>
            </ng-container>

            <ng-container matColumnDef="dueDate">
                <th mat-header-cell *matHeaderCellDef>Due Date</th>
                <td mat-cell *matCellDef="let row">
                    <span class="date-chip">{{ dueDate(row) }}</span>
                </td>
            </ng-container>

            <ng-container matColumnDef="amount">
                <th mat-header-cell *matHeaderCellDef class="align-right">Amount</th>
                <td mat-cell *matCellDef="let row" class="align-right">
                    {{ FormatUtil.formatCurrency(row.amountInCents / 100) }}
                </td>
            </ng-container>

            <ng-container matColumnDef="batch">
                <th mat-header-cell *matHeaderCellDef>Batch</th>
                <td mat-cell *matCellDef="let row">
                    <span class="batch-cell">
                        <span class="batch-label">{{ batchLabel(row) }}</span>
                        <span class="batch-date">{{ batchDate(row) }}</span>
                        <mat-icon class="batch-arrow">chevron_right</mat-icon>
                    </span>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row
                *matRowDef="let row; columns: displayedColumns; trackBy: trackByRow"
                class="search-row"
                role="button"
                tabindex="0"
                matTooltip="Open the batch holding this transaction"
                matTooltipShowDelay="600"
                (click)="onRowClick(row)"
                (keydown.enter)="onRowClick(row)"></tr>
        </table>

        <!-- Progress belongs with the results, not on the control that asked for them.
             The rows are cleared the moment the text changes, so there is never a stale
             table under this — the spinner is the whole state. -->
        <div class="results-placeholder" *ngIf="showSpinner">
            <mat-spinner diameter="40"></mat-spinner>
            <p class="searching-label">Searching…</p>
        </div>

        <!-- Opening state: says what the box takes, instead of an empty table frame. -->
        <div class="results-placeholder" *ngIf="showPrompt">
            <mat-icon>manage_search</mat-icon>
            <p>Search across every batch</p>
            <span>Type at least {{ minSearchLength }} characters of a loan number or a customer name.</span>
        </div>

        <div class="results-placeholder" *ngIf="showEmptyState">
            <mat-icon>search_off</mat-icon>
            <p>No transactions match "{{ term }}"</p>
            <span>Check the spelling, or try the loan number instead of the name.</span>
        </div>

        <!-- A failed query is reported here, in place of the results, and never as a
             message under the input: "nothing came back" is an answer about the results,
             and showing it on the field would read as "what you typed is invalid". -->
        <div class="results-placeholder is-error" *ngIf="errorMessage">
            <mat-icon>error_outline</mat-icon>
            <p>Search failed</p>
            <span>{{ errorMessage }}</span>
            <button mat-stroked-button type="button" class="retry-button" (click)="onRetry()">
                <mat-icon>refresh</mat-icon>
                Try again
            </button>
        </div>

    </div>

    <mat-paginator *ngIf="totalRows && !errorMessage"
        [length]="totalRows"
        [pageSize]="pageSize"
        [pageSizeOptions]="[10, 25, 50]"
        [pageIndex]="currentPage"
        showFirstLastButtons
        (page)="onPageChange($event)">
    </mat-paginator>

</mat-dialog-content>
